// Layer registration for the "before" (left) map of the comparison swipe.
// Lighter than the main map — only the layers that need to differ between
// halves, plus parcel hit-detection so clicks land on either side.
//
// Vector layers come from PMTiles archives via the `pmtiles://` protocol
// (registered once in agricultural-lulc-page.tsx).

import type maplibregl from "maplibre-gl";
import {
  PMTILES_SOURCES,
  PMTILES_SOURCE_LAYERS,
  LULC_RASTER_TILES,
  LULC_RASTER_MIN_ZOOM,
  LULC_RASTER_MAX_ZOOM,
  CROPS_RASTER_TILES,
  CROPS_RASTER_MIN_ZOOM,
  CROPS_RASTER_MAX_ZOOM,
  HARVEST_RASTER_TILES,
  HARVEST_RASTER_MIN_ZOOM,
  HARVEST_RASTER_MAX_ZOOM,
  FOREST_RASTER_TILES,
  FOREST_RASTER_MIN_ZOOM,
  FOREST_RASTER_MAX_ZOOM,
} from "../config/agricultural-lulc";

function pmtilesUrl(absolutePath: string): string {
  return `pmtiles://${window.location.origin}${absolutePath}`;
}

// Before map mirrors the thematic layers the user has visible on the main
// map. LULC is the only layer with two years — left always shows 2024 (the
// temporal counterpart to the main map's 2025). Forest, Crops and parcels
// use the exact same source on both sides; the swipe just reveals what's
// behind them.
export async function registerBeforeMapLayers(bm: maplibregl.Map): Promise<void> {
  if (!bm.getSource("lga-boundary")) {
    bm.addSource("lga-boundary", {
      type: "vector",
      url: pmtilesUrl(PMTILES_SOURCES.lgaBoundary),
    });
    bm.addLayer({
      id: "lga-boundary-fill",
      type: "fill",
      source: "lga-boundary",
      "source-layer": PMTILES_SOURCE_LAYERS.lgaBoundary,
      paint: { "fill-color": "#6366f1", "fill-opacity": 0.05 },
    });
    bm.addLayer({
      id: "lga-boundary-line",
      type: "line",
      source: "lga-boundary",
      "source-layer": PMTILES_SOURCE_LAYERS.lgaBoundary,
      paint: { "line-color": "#6366f1", "line-width": 2.5, "line-dasharray": [3, 2] },
    });
  }

  if (!bm.getSource("lulc-raster-2024")) {
    bm.addSource("lulc-raster-2024", {
      type: "raster",
      tiles: [LULC_RASTER_TILES["2024"]],
      tileSize: 256,
      minzoom: LULC_RASTER_MIN_ZOOM,
      maxzoom: LULC_RASTER_MAX_ZOOM,
    });
    bm.addLayer({
      id: "lulc-raster-2024-fill",
      type: "raster",
      source: "lulc-raster-2024",
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }

  if (!bm.getSource("forest-raster-2024")) {
    bm.addSource("forest-raster-2024", {
      type: "raster",
      tiles: [FOREST_RASTER_TILES],
      tileSize: 256,
      minzoom: FOREST_RASTER_MIN_ZOOM,
      maxzoom: FOREST_RASTER_MAX_ZOOM,
    });
    bm.addLayer({
      id: "forest-raster-2024-fill",
      type: "raster",
      source: "forest-raster-2024",
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }

  if (!bm.getSource("crops-raster-summer2024")) {
    bm.addSource("crops-raster-summer2024", {
      type: "raster",
      tiles: [CROPS_RASTER_TILES],
      tileSize: 256,
      minzoom: CROPS_RASTER_MIN_ZOOM,
      maxzoom: CROPS_RASTER_MAX_ZOOM,
    });
    bm.addLayer({
      id: "crops-raster-summer2024-fill",
      type: "raster",
      source: "crops-raster-summer2024",
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }

  if (!bm.getSource("harvest-raster-summer2024")) {
    bm.addSource("harvest-raster-summer2024", {
      type: "raster",
      tiles: [HARVEST_RASTER_TILES],
      tileSize: 256,
      minzoom: HARVEST_RASTER_MIN_ZOOM,
      maxzoom: HARVEST_RASTER_MAX_ZOOM,
    });
    bm.addLayer({
      id: "harvest-raster-summer2024-fill",
      type: "raster",
      source: "harvest-raster-summer2024",
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }

  if (!bm.getSource("parcels")) {
    bm.addSource("parcels", {
      type: "vector",
      url: pmtilesUrl(PMTILES_SOURCES.parcels),
    });
    bm.addLayer({
      id: "parcels-fill",
      type: "fill",
      source: "parcels",
      "source-layer": PMTILES_SOURCE_LAYERS.parcels,
      // Click hit-detection layer — invisible but queryable.
      paint: { "fill-opacity": 0 },
      layout: { visibility: "none" },
    });
    bm.addLayer({
      id: "parcels-line",
      type: "line",
      source: "parcels",
      "source-layer": PMTILES_SOURCE_LAYERS.parcels,
      paint: {
        "line-color": "#a78bfa",
        "line-width": 1.5,
        "line-opacity": 0,
      },
      layout: { visibility: "none" },
    });
    bm.addLayer({
      id: "parcels-highlight",
      type: "line",
      source: "parcels",
      "source-layer": PMTILES_SOURCE_LAYERS.parcels,
      paint: {
        "line-color": "#facc15",
        "line-width": 3,
        "line-opacity": 0,
      },
      filter: ["==", ["get", "cadastre_polygon_pid"], ""],
      layout: { visibility: "visible" },
    });
  }
}
